import { useMemo, useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'
import type { ContextToggles } from '@/types'

/** Human-readable labels for each toggle */
const TOGGLE_LABELS: Record<keyof ContextToggles, string> = {
  portfolio: 'Portfolio',
  constitution: 'Constitution',
  tmfRankings: 'TMF Rankings',
  watchlist: 'Watchlist',
  rulesScoring: 'Rules & Scoring',
}

const TOGGLE_KEYS: (keyof ContextToggles)[] = [
  'portfolio',
  'constitution',
  'tmfRankings',
  'watchlist',
  'rulesScoring',
]

interface ContextTogglePanelProps {
  toggles: ContextToggles
  onChange: (toggles: ContextToggles) => void
  constitutionTokenCount?: number
  selectedAccounts?: string[]
}

export function ContextTogglePanel({
  toggles,
  onChange,
  constitutionTokenCount,
  selectedAccounts,
}: ContextTogglePanelProps) {
  // Fetch real data sizes from Supabase for accurate token estimates
  const [dataSizes, setDataSizes] = useState({ trades: 0, positions: 0, watchlist: 0, constitution: 0, tmf: 0 })

  useEffect(() => {
    async function fetchSizes() {
      const sizes = { trades: 0, positions: 0, watchlist: 0, constitution: 0, tmf: 0 }
      try {
        // Count trades (open)
        let q = supabase.from('trades').select('id', { count: 'exact', head: true }).eq('status', 'open')
        if (selectedAccounts && selectedAccounts.length > 0) q = q.in('account_slug', selectedAccounts)
        const { count: openCount } = await q
        // Count closed trades (always last 50)
        sizes.trades = ((openCount || 0) + 50) * 350 // ~350 chars per trade row in JSON

        // Count positions
        const { count: posCount } = await supabase.from('positions').select('id', { count: 'exact', head: true })
        sizes.positions = (posCount || 0) * 200

        // Watchlist size
        const { count: wlCount } = await supabase.from('watchlist').select('id', { count: 'exact', head: true })
        sizes.watchlist = (wlCount || 0) * 300 // ~300 chars per stock row

        // Constitution
        const { data: con } = await supabase.from('constitution').select('content').eq('is_active', true).limit(1).single()
        sizes.constitution = con?.content ? (con.content as string).length : 0

        // TMF Rankings
        const { count: tmfCount } = await supabase.from('tmf_rankings').select('id', { count: 'exact', head: true })
        sizes.tmf = (tmfCount || 0) * 60

        setDataSizes(sizes)
      } catch { /* non-fatal */ }
    }
    fetchSizes()
  }, [selectedAccounts])

  const tokenEstimate = useMemo(() => {
    let totalChars = 0
    if (toggles.portfolio) totalChars += dataSizes.trades + dataSizes.positions + 2000 // +2000 for cash controls + VIX
    if (toggles.constitution) totalChars += constitutionTokenCount ? constitutionTokenCount * 4 : dataSizes.constitution
    if (toggles.tmfRankings) totalChars += dataSizes.tmf
    if (toggles.watchlist) totalChars += dataSizes.watchlist
    if (toggles.rulesScoring) totalChars += 3000
    // Convert chars to tokens (1 token ≈ 4 chars)
    return Math.ceil(totalChars / 4)
  }, [toggles, dataSizes, constitutionTokenCount])

  const handleToggle = (key: keyof ContextToggles) => {
    onChange({ ...toggles, [key]: !toggles[key] })
  }

  const formatTokens = (count: number): string => {
    if (count === 0) return '0 tokens'
    return `~${count.toLocaleString()} tokens`
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      {TOGGLE_KEYS.map(key => (
        <button
          key={key}
          onClick={() => handleToggle(key)}
          className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
            toggles[key]
              ? 'bg-[var(--color-accent)]/20 border-[var(--color-accent)] text-[var(--color-accent)] font-medium'
              : 'border-[var(--color-border)] text-[var(--color-text-muted)] hover:border-[var(--color-text-muted)]'
          }`}
          aria-pressed={toggles[key]}
          aria-label={`Toggle ${TOGGLE_LABELS[key]} context`}
        >
          <span className="mr-1.5">{toggles[key] ? '●' : '○'}</span>
          {TOGGLE_LABELS[key]}
        </button>
      ))}
      <span className="ml-auto text-xs text-[var(--color-text-muted)]">
        {formatTokens(tokenEstimate)}
      </span>
    </div>
  )
}
